/*
 * Floating WhatsApp button (snippets/whatsapp-button.liquid).
 *
 * Builds the wa.me link with a pre-filled message (greeting + current page URL)
 * and, on click, fires a fire-and-forget beacon to our ops POST /wa-click so the
 * team can later match an incoming WhatsApp message to its source page + referral.
 *
 * The button opens WhatsApp regardless of whether the beacon succeeds — the log
 * must never block or break the redirect. Referral attribution rides in our
 * wa_leads table (window.bqGetRef), NOT in the message text. The text param is
 * for the human reading it in WhatsApp. See ops/plans/whatsapp-button.md.
 */
(function () {
  var btn = document.querySelector('[data-wa-button]');
  if (!btn) return;

  var number = (btn.dataset.waNumber || '').replace(/\D/g, '');
  if (!number) return;

  var pageUrl = btn.dataset.waUrl || window.location.href;
  var greeting = btn.dataset.waGreeting || 'Hello. I have a question about';

  // Build the wa.me link with the pre-filled message. Done up front so the link
  // works even if the click handler never runs.
  var text = greeting.replace(/\s+$/, '') + ' ' + pageUrl;
  btn.href = 'https://wa.me/' + number + '?text=' + encodeURIComponent(text);

  function logClick() {
    var url = btn.dataset.waClickUrl;
    if (!url) return; // logging disabled (no endpoint configured)
    var ga = (window.bqGetGa && window.bqGetGa()) || {};

    var payload = {
      source_url: pageUrl,
      product_url: btn.dataset.waProductUrl || null,
      product_title: btn.dataset.waProductTitle || null,
      referral_code: (window.bqGetRef && window.bqGetRef()) || null,
      utm: (window.bqGetUtm && window.bqGetUtm()) || {},
      landing: (window.bqGetLanding && window.bqGetLanding()) || null,
      // First-touch external referrer (assets/utm.js) — the only trace of untagged sources
      // such as organic Google or AI answers. The team matches each tap to the inquiry it
      // became, and this is what names its origin (ops/plans/whatsapp-click-attribution.md).
      referrer: (window.bqGetReferrer && window.bqGetReferrer()) || null,
      // Meta identifiers (assets/utm.js) — the server turns this tap into a Contact
      // event, and since a tap carries no email these cookies are the only match keys
      // it has. Without them the event is dropped server-side rather than sent blind.
      fbp: (window.bqGetFbp && window.bqGetFbp()) || null,
      fbc: (window.bqGetFbc && window.bqGetFbc()) || null,
      // GA4 cookies — the server turns this tap into the whatsapp_click key event.
      ga_cookie: ga.client || null,
      ga_session_cookie: ga.session || null
    };

    var body = JSON.stringify(payload);

    // sendBeacon is the most reliable during/after navigation; fall back to a
    // keepalive fetch. Either way this is best-effort — failures are swallowed.
    try {
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) return;
      }
    } catch (e) {
      /* fall through to fetch */
    }

    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true,
        mode: 'cors'
      }).catch(function () {});
    } catch (e) {
      /* give up silently — the WhatsApp redirect still happens */
    }
  }

  // target="_blank" opens WhatsApp in a new tab, so we don't preventDefault — the
  // current page stays put and the beacon goes out on the same tick.
  btn.addEventListener('click', logClick);

  // Every other wa.me link on the site (contact page, footer, consultation modal) is
  // the same intent and gets the same beacon — the owner counts a WhatsApp tap from any
  // page as a key event (ops/plans/ga4-lead-key-events.md). Delegated from the document
  // so links rendered later (modals) are covered; the button and the nudge link have
  // their own handlers and are skipped here so a tap is logged once.
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href*="wa.me/"]') : null;
    if (!a || a === btn || a.hasAttribute('data-wa-nudge-link')) return;
    logClick();
  });

  /* ── The nudge ──────────────────────────────────────────────────────────────────
   * A WhatsApp-looking message that appears above the button once someone has spent
   * about two minutes on the site: long enough to be looking for something, and to
   * not have found it.
   *
   * Two minutes ON THE SITE, not on the page. A visitor who reads four category pages
   * for thirty seconds each is exactly who this is for, and a per-page timer would
   * never fire for them. The clock starts once per tab and survives navigation in
   * sessionStorage.
   *
   * Shown at most once per visit, and dismissing it silences the nudge for a week.
   * Anything more insistent is a pop-up, and a pop-up on every page is the reason
   * people learn to close things without reading them.
   */
  var NUDGE_AFTER_MS = 120000;
  var NUDGE_MUTE_DAYS = 7;
  var CLOCK_KEY = 'bq_wa_nudge_start';   // when this visit began (sessionStorage)
  var SHOWN_KEY = 'bq_wa_nudge_shown';   // already surfaced this visit (sessionStorage)
  var MUTE_KEY = 'bq_wa_nudge_muted';    // dismissed at (localStorage, survives visits)

  var nudge = document.querySelector('[data-wa-nudge]');
  if (!nudge) return;

  // Every storage read is wrapped: Safari in private mode throws on access, and a
  // thrown error here would take the WhatsApp button down with it.
  function read(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }
  function write(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) { /* private mode */ }
  }

  function muted() {
    var at = parseInt(read('localStorage', MUTE_KEY), 10);
    if (!at) return false;
    return Date.now() - at < NUDGE_MUTE_DAYS * 86400000;
  }

  function hide() {
    nudge.classList.remove('is-visible');
    // Match the CSS transition so it fades rather than vanishing mid-animation.
    window.setTimeout(function () { nudge.hidden = true; }, 250);
  }

  function show() {
    if (muted() || nudge.hidden === false) return;
    nudge.hidden = false;
    // Two frames: the element has to be laid out at its start state before the class
    // that animates away from it lands, or the browser skips the transition.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { nudge.classList.add('is-visible'); });
    });
    write('sessionStorage', SHOWN_KEY, '1');
  }

  var link = nudge.querySelector('[data-wa-nudge-link]');
  if (link) {
    link.href = btn.href;          // same chat, same pre-filled message as the button
    link.addEventListener('click', function () {
      logClick();                  // same attribution as a tap on the button
      hide();
    });
  }

  var closeBtn = nudge.querySelector('[data-wa-nudge-close]');
  if (closeBtn) {
    closeBtn.addEventListener('click', function () {
      write('localStorage', MUTE_KEY, String(Date.now()));
      hide();
    });
  }

  if (read('sessionStorage', SHOWN_KEY) || muted()) return;

  var startedAt = parseInt(read('sessionStorage', CLOCK_KEY), 10);
  if (!startedAt) {
    startedAt = Date.now();
    write('sessionStorage', CLOCK_KEY, String(startedAt));
  }

  var remaining = NUDGE_AFTER_MS - (Date.now() - startedAt);
  // Already past two minutes when this page opened (they navigated) — still give the
  // page a moment to settle, so the nudge does not arrive on top of a loading screen.
  window.setTimeout(show, remaining > 0 ? remaining : 1500);
})();
